import { useState, useEffect, useCallback, useMemo, useRef } from "react";

// Reusable server-side pagination for the organization-side list tables.
//
// The backend list APIs return the standard paginated wrapper:
//   { items, totalCount, pageNumber, pageSize, orderByLatest, totalPages }
// with a default pageSize of 10 and a max of 30.
//
// Usage:
//   const { items, loading, paginationProps, refresh } =
//     useServerPagination((params) => fetchLearners(params));
//   <Table dataSource={items} loading={loading} pagination={paginationProps} />
//
// Extra query params (search, a business-group id, a mentor id, etc.) can be
// seeded via initialFilters and updated later with setFilter (which also resets
// back to page 1). The fetch function receives the full filters object, so it
// can decide whether a value is a query param or a path segment.

const DEFAULT_PAGE_SIZE = 10;
const MAX_PAGE_SIZE = 30; // backend cap

const emptyResult = (pageSize = DEFAULT_PAGE_SIZE) => ({
  items: [],
  totalCount: 0,
  pageNumber: 1,
  pageSize,
  totalPages: 1,
});

export const useServerPagination = (fetchFn, initialFilters = {}, options = {}) => {
  const { pageSize = DEFAULT_PAGE_SIZE, auto = true, showSizeChanger = true, onError } = options;

  // Keep the latest fetchFn / onError in refs so callers can pass inline
  // functions without retriggering the effect on every render.
  const fetchRef = useRef(fetchFn);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    fetchRef.current = fetchFn;
    onErrorRef.current = onError;
  });

  const [data, setData] = useState(() => emptyResult(pageSize));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(() => ({
    pageNumber: 1,
    pageSize,
    orderByLatest: false,
    ...initialFilters,
  }));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchRef.current(filters);

      // Normal case: the paginated wrapper.
      if (res && Array.isArray(res.items)) {
        setData({
          items: res.items,
          totalCount: res.totalCount ?? res.items.length,
          pageNumber: res.pageNumber ?? filters.pageNumber,
          pageSize: res.pageSize ?? filters.pageSize,
          totalPages: res.totalPages ?? 1,
        });
      } else if (Array.isArray(res)) {
        // Defensive: some endpoints may still return a plain array.
        setData({
          items: res,
          totalCount: res.length,
          pageNumber: 1,
          pageSize: res.length || filters.pageSize,
          totalPages: 1,
        });
      } else {
        setData(emptyResult(filters.pageSize));
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to load data");
      setData(emptyResult(filters.pageSize));
      if (onErrorRef.current) onErrorRef.current(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (auto) load();
  }, [auto, load]);

  // Table onChange gives (pageNumber, pageSize).
  const handlePageChange = useCallback((pageNumber, newPageSize) => {
    setFilters((prev) => ({
      ...prev,
      pageNumber,
      pageSize: newPageSize ? Math.min(newPageSize, MAX_PAGE_SIZE) : prev.pageSize,
    }));
  }, []);

  // Update filters/search and reset to the first page.
  const setFilter = useCallback((patch) => {
    setFilters((prev) => ({ ...prev, ...patch, pageNumber: 1 }));
  }, []);

  // Ready to spread onto an AntD <Table pagination={...}> or <Pagination {...}>.
  const paginationProps = useMemo(
    () => ({
      current: data.pageNumber,
      pageSize: data.pageSize,
      total: data.totalCount,
      onChange: handlePageChange,
      showSizeChanger,
      pageSizeOptions: ["10", "20", "30"],
      showTotal: (total, range) =>
        total > 0 ? `${range[0]}-${range[1]} of ${total}` : "",
    }),
    [data, handlePageChange, showSizeChanger]
  );

  return {
    items: data.items,
    data,
    loading,
    error,
    filters,
    setFilter,
    handlePageChange,
    paginationProps,
    refresh: load,
  };
};

export default useServerPagination;
